import React from 'react';
import { Button, Icon, IconButton, useDisclosure } from '@chakra-ui/react';
import { FiFileText } from 'react-icons/fi';
import CustomTooltip from 'components/shared/CustomTooltip';
import { usePermissions } from 'hooks/usePermissions';

import TemplatePicker from './TemplatePicker';

/**
 * The one control that opens the template picker.
 *
 * ★ IT USED TO SEND ONE HARD-CODED TEMPLATE.
 * `hello_world` — Meta's parameterless sample — was the honest offer while
 * nothing could list a workspace's own catalogue: one real template rather than
 * a picker pretending to have many. `GET /conversations/:id/templates` now
 * answers that, authorised by the same question the send asks, so the button
 * opens the real thing. The name, the props and the file are unchanged so every
 * caller gained it without an edit.
 *
 * ★ ONE COMPONENT, FOUR PLACES. The composer's icon row, the two blocked
 * strips, and the banner above a thread nobody has replied to. The permission
 * check and the picker are identical in all of them; four copies would drift
 * the moment any one changed.
 *
 * ★ THE PERMISSION CHECK HERE IS COSMETIC, NOT A GUARD. The server resolves
 * `message.send.template` on both the list and the send. Hiding the button only
 * avoids offering something that would answer 403.
 */

/**
 * @param {string} conversationId
 * @param {'icon'|'button'} as  an icon for the composer row, a labelled button elsewhere
 */
export default function SendTemplateButton({
	conversationId,
	as = 'button',
	size = 'xs',
	variant = 'outline',
	isDisabled = false,
}) {
	const { hasPermission } = usePermissions();
	const picker = useDisclosure();

	if (!hasPermission('whatsapp', 'message.send.template') || !conversationId) return null;

	// ★ MOUNTED ALONGSIDE THE TRIGGER, NOT INSIDE IT.
	// A Modal rendered as a child of a Button would inherit its disabled and
	// pointer states; kept as a sibling it opens from the composer, from a
	// blocked strip, and from inside the lead drawer with no special case.
	const picked = (
		<TemplatePicker isOpen={picker.isOpen} onClose={picker.onClose} conversationId={conversationId} />
	);

	if (as === 'icon') {
		return (
			<>
				<CustomTooltip label="Send a template">
					<IconButton
						aria-label="Send a template"
						icon={<Icon as={FiFileText} boxSize="20px" />}
						variant="ghost"
						size="sm"
						borderRadius="full"
						color="wa.inkMuted"
						_hover={{ bg: 'wa.hover', color: 'wa.ink' }}
						onClick={picker.onOpen}
						isDisabled={isDisabled}
					/>
				</CustomTooltip>
				{picked}
			</>
		);
	}

	return (
		<>
			<Button
				size={size}
				variant={variant}
				leftIcon={<Icon as={FiFileText} />}
				onClick={picker.onOpen}
				isDisabled={isDisabled}
			>
				Send a template
			</Button>
			{picked}
		</>
	);
}
